import InventoryLogType from './enums/InventoryLogType';
import InventoryItemStatusTitle from './enums/InventoryItemStatusTitle';
import InventoryHolderTypeTitle from './enums/InventoryHolderTypeTitle';

/**
 * Русские описания событий ленты истории — собираются по type и payload.
 *
 * Журнал пишет action на английском и человеку не предназначен: это машинный след для
 * поддержки. Лента показывает текст, собранный здесь, а англоязычный action остаётся
 * запасным вариантом для кодов, которых этот модуль не знает, — накопленных записей
 * и событий, которые появятся в бэкенде раньше, чем будет выкачен фронт.
 *
 * Поэтому ни одна ветка не бросает исключений и не возвращает пустую строку: либо готовый
 * текст, либо null — «строй сам из description».
 */

/**
 * Подписи полей предмета для события «данные изменены».
 */
const ITEM_FIELD_TITLES = {
    name: 'название',
    cost: 'цена',
    delivered_at: 'дата поставки',
    warranty_expire_at: 'срок гарантии',
    qr_code_url: 'QR-код',
    city_id: 'город',
    status: 'статус',
    responsible_person_id: 'ответственный',
    holder_id: 'ответственный',
    holder_expected_at: 'дата начала владения',
    holder_until_at: 'дата возврата',
    creator_id: 'автор записи',
};

/**
 * Служебные ключи, которые логгер добавляет в details сам. В описании им не место.
 */
const SERVICE_FIELDS = ['id', 'item_id', 'creator_id', 'created_at', 'updated_at'];

/**
 * Дата из payload (формат Y-m-d) в привычный d.m.Y. Значения неожиданного вида
 * возвращаем как есть: в описании лучше сырая строка, чем «Invalid date».
 *
 * @param {string|null} value
 * @returns {string|null}
 */
function formatDate(value) {
    if (!value) {
        return null;
    }

    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);

    return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value);
}

/**
 * Название статуса по числовому значению.
 *
 * @param {number|null} value
 * @returns {string}
 */
function statusTitle(value) {
    if (value === null || value === undefined) {
        return 'не задан';
    }

    return InventoryItemStatusTitle[value] ?? String(value);
}

/**
 * Держатель одной из сторон события: old или new.
 *
 * Вид держателя выводится из того, какая ссылка непустая, — ровно как на бэкенде.
 * Если ссылка есть, а имени нет (запись справочника могла исчезнуть, учётка — быть
 * удалена), показываем номер: это лучше, чем молча потерять сторону события.
 *
 * @param {object} payload
 * @param {string} prefix 'old' или 'new'
 * @returns {string|null}
 */
function holderLabel(payload, prefix) {
    const personName = payload[`${prefix}_responsible_person_name`];
    const personId = payload[`${prefix}_responsible_person_id`];

    if (personName) {
        return `${personName} (сотрудник Service Desk)`;
    }

    const holderName = payload[`${prefix}_holder_name`];
    const holderId = payload[`${prefix}_holder_id`];
    const holderTypeTitle = InventoryHolderTypeTitle[payload[`${prefix}_holder_type`]];

    if (holderName) {
        return holderTypeTitle ? `${holderName} (${holderTypeTitle.toLowerCase()})` : holderName;
    }

    // Имени нет, а ссылка есть: учётку удалили или запись справочника исчезла.
    // Сторону события всё равно называем, но без внутреннего идентификатора —
    // номер строки в базе пользователю ничего не говорит.
    if (personId) {
        return 'сотрудник Service Desk';
    }

    if (holderId) {
        return 'запись справочника';
    }

    return null;
}

/**
 * Хвост про сроки владения. Пустая строка означает «о датах говорить нечего».
 *
 * @param {object} payload
 * @returns {string}
 */
function holderDatesText(payload) {
    const expected = formatDate(payload.new_holder_expected_at);
    const until = formatDate(payload.new_holder_until_at);
    const parts = [];

    if (expected) {
        parts.push(`с ${expected}`);
    }

    if (until) {
        parts.push(`по ${until}`);
    }

    if (parts.length) {
        return `Срок владения: ${parts.join(' ')}.`;
    }

    const hadDates = Boolean(payload.old_holder_expected_at || payload.old_holder_until_at);

    return hadDates ? 'Срок владения снят.' : '';
}

/**
 * @param {object} payload
 * @returns {string}
 */
function describeHolderChanged(payload) {
    const before = holderLabel(payload, 'old');
    const after = holderLabel(payload, 'new');
    const dates = holderDatesText(payload);

    let text;

    if (before && after && before === after) {
        text = `Ответственный прежний: ${after}.`;
    } else if (before && after) {
        text = `Ответственный изменён: ${before} → ${after}.`;
    } else if (after) {
        text = `Ответственный назначен: ${after}.`;
    } else if (before) {
        text = `Ответственный снят, был ${before}.`;
    } else {
        text = 'Ответственный не назначен.';
    }

    return dates ? `${text} ${dates}` : text;
}

/**
 * Легаси-код: так писались смены материально ответственного до появления держателей.
 *
 * @param {object} payload
 * @returns {string|null}
 */
function describeResponsibleChanged(payload) {
    const before = payload.old_responsible_person_name;
    const after = payload.new_responsible_person_name;

    if (!before && !after) {
        return null;
    }

    if (before && after) {
        return `Материально ответственный изменён: ${before} → ${after}.`;
    }

    return after ? `Материально ответственный назначен: ${after}.` : `Материально ответственный снят, был ${before}.`;
}

/**
 * @param {object} payload
 * @returns {string}
 */
function describeStatusChanged(payload) {
    return `Статус: ${statusTitle(payload.old_status)} → ${statusTitle(payload.new_status)}.`;
}

/**
 * Операции InventoryItemActionService::logAction(): ключ — строка, которую бэкенд кладёт
 * в business_action. Формулировки — те же, что у деловой истории (InventoryItemEventTypeTitle).
 */
const BUSINESS_ACTION_TITLES = {
    'issued': 'Выдано.',
    'returned': 'Возвращено на склад.',
    'holder changed': 'Ответственный изменён.',
    'moved': 'Перемещено.',
    'sent to repair': 'Отправлено в ремонт.',
    'returned from repair': 'Возвращено из ремонта.',
    'marked broken': 'Обнаружена неисправность.',
    'fault cleared': 'Отметка о неисправности снята.',
    'written off': 'Списано.',
};

/**
 * @param {object} payload
 * @returns {string|null}
 */
function describeItemUpdated(payload) {
    // Действия жизненного цикла (7-F2) пишут в технический журнал тот же ITEM_UPDATED, но без
    // changes — с названием операции. Без этой ветки лента показывала английский audit-текст
    // «An inventory item … has been moved by …», в том числе в «Последних событиях» карточки.
    if (payload.business_action) {
        return BUSINESS_ACTION_TITLES[payload.business_action] || 'Выполнено действие с оборудованием.';
    }

    const changes = payload.changes;

    if (!changes || typeof changes !== 'object') {
        return null;
    }

    // Показываем только поля с человеческой подписью. Незнакомое имя колонки раньше
    // уходило в текст как есть — пользователь видел «warranty_expire_at» вместо
    // названия поля, а имена внутренних колонок ему показывать незачем.
    const fields = Object.keys(changes)
        .filter((field) => SERVICE_FIELDS.indexOf(field) === -1)
        .map((field) => ITEM_FIELD_TITLES[field])
        .filter(Boolean);

    return fields.length ? `Изменены поля: ${fields.join(', ')}.` : null;
}

/**
 * @param {object} payload
 * @returns {string|null}
 */
function describeItemCreated(payload) {
    const name = payload.initial_data ? payload.initial_data.name : null;

    return name ? `Заведено оборудование «${name}».` : null;
}

/**
 * Событие вложения: имя файла, назначение и подпись.
 *
 * Ссылки на файл здесь нет намеренно. Лента истории — это след событий, и след остаётся
 * после того, как файл удалили; ссылка в такой строке вела бы в никуда. Смотреть вложения
 * ходят во вкладку «Файлы», а лента отвечает на вопрос «что с этим предметом делали».
 *
 * @param {object} payload
 * @param {string} verb Глагол события: «Прикреплён» или «Удалён».
 * @returns {string|null}
 */
function describeFileEvent(payload, verb) {
    const name = payload.file_name;

    if (!name) {
        return null;
    }

    // category_title приходит с бэкенда готовым: категорий восемь, и вторая их таблица
    // здесь разошлась бы с первой на первом же добавлении.
    const category = payload.category_title;
    const comment = payload.comment;

    let text = `${verb} файл «${name}»`;

    if (category) {
        text += `, категория «${category}»`;
    }

    return comment ? `${text}. ${comment}` : `${text}.`;
}

/**
 * Событие главного фото.
 *
 * Отдельно от describeFileEvent(): здесь важно не назначение кадра, а замена — «вместо А
 * теперь Б». Категория в этой фразе лишняя, а имя прежнего кадра, наоборот, половина смысла:
 * без него из ленты не понять, была ли замена или фото появилось впервые.
 *
 * @param {object} payload
 * @param {boolean} primary
 * @returns {string|null}
 */
function describePrimaryPhotoEvent(payload, primary) {
    const name = payload.file_name;

    if (!name) {
        return null;
    }

    if (!primary) {
        return `Снят признак главного фото с «${name}».`;
    }

    const previous = payload.previous_file_name;

    return previous ? `Главным фото стало «${name}» вместо «${previous}».` : `Главным фото стало «${name}».`;
}

/**
 * Атрибут в виде «тип: значение». Источник разный: при создании это initial_data,
 * при удалении — полный снимок строки, при изменении — только изменённые ключи.
 *
 * @param {object|null} attribute
 * @returns {string|null}
 */
function attributeText(attribute) {
    if (!attribute || typeof attribute !== 'object') {
        return null;
    }

    const type = attribute.type;
    const value = attribute.value;

    // Без названия атрибута событие непредставимо: «: Ижевск» пользователю ничего
    // не объясняет. Такое событие лента скрывает, а не показывает наполовину.
    if (!type) {
        return null;
    }

    return value === undefined || value === null || value === '' ? `„${type}“` : `„${type}“: ${value}`;
}

/**
 * Значение атрибута из payload в строку для сравнения и показа.
 *
 * null, undefined и пустая строка — одно и то же состояние «значения нет»: атрибут
 * могли очистить и записью null, и записью пустой строки, а пользователю разницы нет.
 *
 * @param {*} value
 * @returns {string}
 */
function attributeValueText(value) {
    return value === null || value === undefined ? '' : String(value);
}

/**
 * Описание изменения атрибута.
 *
 * Собирается только из нового payload, где есть название и обе стороны изменения.
 * Накопленные записи такого payload не имеют: там лежит один getDirty(), то есть новое
 * значение без названия атрибута и без старого. Их лента по-прежнему скрывает —
 * старую историю не мигрируем, и показать в ней нечего.
 *
 * @param {object} payload
 * @returns {string|null}
 */
function describeAttributeUpdated(payload) {
    const type = payload.type;

    // Старый неполный payload: названия атрибута нет, событие непредставимо.
    if (!type) {
        return null;
    }

    const before = attributeValueText(payload.old);
    const after = attributeValueText(payload.new);

    // Значение не изменилось — тронули только технический флаг (searchable).
    // Показывать «изменён» там, где пользователь не увидит разницы, нельзя.
    if (before === after) {
        return null;
    }

    if (!before) {
        return `Заполнен атрибут „${type}“: ${after}`;
    }

    if (!after) {
        return `Очищен атрибут „${type}“, был ${before}`;
    }

    return `Изменён атрибут „${type}“: ${before} → ${after}`;
}

/**
 * Человекочитаемое описание события ленты или null, если тип незнаком.
 *
 * @param {object} event Событие из GET /v1/inventory/items/{id}/history.
 * @returns {string|null}
 */
export default function describeInventoryEvent(event) {
    const payload = event && event.payload ? event.payload : null;

    if (!payload) {
        return null;
    }

    switch (event.type) {
        case InventoryLogType.ITEM_CREATED:
            return describeItemCreated(payload);

        case InventoryLogType.ITEM_UPDATED:
            return describeItemUpdated(payload);

        case InventoryLogType.ITEM_STATUS_CHANGED:
            return describeStatusChanged(payload);

        case InventoryLogType.ITEM_HOLDER_CHANGED:
            return describeHolderChanged(payload);

        case InventoryLogType.ITEM_RESPONSIBLE_CHANGED:
            return describeResponsibleChanged(payload);

        case InventoryLogType.ITEM_ATTRIBUTE_CREATED: {
            const text = attributeText(payload.initial_data);
            return text ? `Добавлен атрибут ${text}` : null;
        }

        // Новые события несут название атрибута и обе стороны изменения и показываются
        // как есть. Накопленные записи такого payload не имеют — describeAttributeUpdated()
        // вернёт для них null, и лента их скроет, как и раньше. Английский audit-текст
        // с идентификатором строки не показывается ни в том, ни в другом случае:
        // фолбэк для атрибутных событий отключён в самом компоненте ленты.
        case InventoryLogType.ITEM_ATTRIBUTE_UPDATED:
            return describeAttributeUpdated(payload);

        case InventoryLogType.ITEM_ATTRIBUTE_DELETED: {
            const text = attributeText(payload.attributes);
            return text ? `Удалён атрибут ${text}` : null;
        }

        case InventoryLogType.ITEM_FILE_ATTACHED:
            return describeFileEvent(payload, 'Прикреплён');

        case InventoryLogType.ITEM_FILE_DELETED:
            return describeFileEvent(payload, 'Удалён');

        case InventoryLogType.ITEM_FILE_PRIMARY_SET:
            return describePrimaryPhotoEvent(payload, true);

        case InventoryLogType.ITEM_FILE_PRIMARY_CLEARED:
            return describePrimaryPhotoEvent(payload, false);

        default:
            return null;
    }
}
